import { useEffect, useRef, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MessageSquare, Send, ArrowLeft, Lock, ChevronRight, ShieldAlert, Flag } from "lucide-react";
import { toast } from "sonner";

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso), diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d`;
  return d.toLocaleDateString();
}

export default function Messages() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [convos, setConvos] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [thread, setThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const scrollRef = useRef(null);
  const banned = !!user?.messaging_banned;

  const loadConvos = useCallback(async () => {
    try { const { data } = await api.get("/conversations"); setConvos(data); }
    catch (err) { console.error("Failed to load conversations:", err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadConvos(); }, [loadConvos]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const c = params.get("c");
    if (c) setActiveId(c);
  }, [location.search]);

  const loadThread = useCallback(async (cid) => {
    try {
      const { data } = await api.get(`/conversations/${cid}/messages`);
      setThread(data.conversation);
      setMessages(data.messages);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  }, []);

  useEffect(() => {
    if (!activeId) { setThread(null); setMessages([]); return; }
    loadThread(activeId).then(() => loadConvos());
    const t = setInterval(() => loadThread(activeId), 7000);
    return () => clearInterval(t);
  }, [activeId, loadThread, loadConvos]);

  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [messages]);

  const send = async () => {
    const text = input.trim();
    if (!text || sending || !activeId) return;
    setSending(true);
    setInput("");
    try {
      const { data } = await api.post(`/conversations/${activeId}/messages`, { text });
      setMessages((m) => [...m, data]);
      loadConvos();
    } catch (err) {
      setInput(text);
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSending(false); }
  };

  const openConvo = (c) => {
    setActiveId(c.id);
    navigate(`/messages?c=${c.id}`, { replace: true });
  };

  const reportMessage = async (m) => {
    const reason = window.prompt("Report this message to an admin? Optionally add a reason:", "");
    if (reason === null) return;
    try {
      const { data } = await api.post(`/conversations/${activeId}/messages/${m.id}/report`, { reason });
      toast.success(data.already ? "You already reported this message." : "Reported to admin. Thank you.");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div className="h-screen flex flex-col bg-ys-canvas">
      <Header />
      <div className="flex-1 min-h-0 max-w-5xl w-full mx-auto flex w-full">
        {/* Conversation list */}
        <div className={`${activeId ? "hidden md:flex" : "flex"} flex-col w-full md:w-2/5 md:border-r border-slate-200 bg-white min-h-0`} data-testid="messages-list-panel">
          <div className="px-5 py-4 border-b border-slate-100 shrink-0">
            <h1 className="font-display text-xl font-extrabold flex items-center gap-2"><MessageSquare className="w-5 h-5 text-ys-sky" />Messages</h1>
            <p className="text-xs text-slate-500 mt-0.5">Private chat — your phone &amp; email stay hidden.</p>
          </div>
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="flex justify-center py-16"><div className="ys-spinner" /></div>
            ) : convos.length === 0 ? (
              <div className="text-center py-16 px-6 text-slate-400" data-testid="messages-empty">
                <MessageSquare className="w-10 h-10 mx-auto mb-2" />
                <p className="font-semibold text-slate-500">No conversations yet</p>
                <p className="text-sm mt-1">Open a sale and tap “Message seller” to start chatting.</p>
                <Button onClick={() => navigate("/browse")} data-testid="messages-browse-btn" className="mt-4 rounded-full bg-ys-sky hover:bg-sky-700">Browse sales</Button>
              </div>
            ) : (
              convos.map((c) => (
                <button key={c.id} onClick={() => openConvo(c)} data-testid={`convo-item-${c.id}`}
                  className={`w-full text-left px-5 py-3.5 border-b border-slate-50 hover:bg-slate-50 flex items-center gap-3 ${activeId === c.id ? "bg-sky-50" : ""}`}>
                  <div className="w-10 h-10 rounded-full bg-ys-sky/10 text-ys-sky flex items-center justify-center font-bold shrink-0">
                    {(c.other_username || "M").slice(0, 2).toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <p className="font-semibold text-sm truncate">@{c.other_username}</p>
                      <span className="text-[11px] text-slate-400 shrink-0">{timeAgo(c.last_message_at)}</span>
                    </div>
                    <p className="text-xs text-slate-500 truncate">{c.listing_title}</p>
                    <p className="text-xs text-slate-400 truncate">{c.last_message || "No messages yet"}</p>
                  </div>
                  {c.unread > 0 && <span data-testid={`convo-unread-${c.id}`} className="shrink-0 min-w-5 h-5 px-1.5 rounded-full bg-ys-sky text-white text-[11px] font-bold flex items-center justify-center">{c.unread}</span>}
                  <ChevronRight className="w-4 h-4 text-slate-300 shrink-0 hidden md:block" />
                </button>
              ))
            )}
          </div>
        </div>

        {/* Thread */}
        <div className={`${activeId ? "flex" : "hidden md:flex"} flex-col w-full md:w-3/5 min-h-0`} data-testid="messages-thread-panel">
          {!activeId ? (
            <div className="flex-1 hidden md:flex flex-col items-center justify-center text-slate-400">
              <MessageSquare className="w-12 h-12 mb-2" />
              <p className="font-semibold text-slate-500">Select a conversation</p>
            </div>
          ) : (
            <>
              <div className="px-4 py-3 border-b border-slate-100 bg-white flex items-center gap-3 shrink-0">
                <button onClick={() => { setActiveId(null); navigate("/messages", { replace: true }); }} data-testid="thread-back-btn" className="md:hidden p-1.5 rounded-lg hover:bg-slate-100"><ArrowLeft className="w-5 h-5" /></button>
                <div className="w-9 h-9 rounded-full bg-ys-sky/10 text-ys-sky flex items-center justify-center font-bold shrink-0">{(thread?.other_username || "M").slice(0, 2).toUpperCase()}</div>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-sm truncate" data-testid="thread-username">@{thread?.other_username || "member"}</p>
                  <button onClick={() => thread?.listing_id && navigate(`/listing/${thread.listing_id}`)} className="text-xs text-ys-sky hover:underline truncate block max-w-full text-left" data-testid="thread-listing-link">{thread?.listing_title}</button>
                </div>
              </div>

              <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-2.5 bg-slate-50" data-testid="thread-messages">
                <div className="flex items-center justify-center">
                  <span className="text-[11px] text-slate-400 bg-white border border-slate-200 rounded-full px-3 py-1 flex items-center gap-1"><Lock className="w-3 h-3" />Contact details stay private in Yard-skip</span>
                </div>
                {messages.map((m) => {
                  const mine = m.sender_id === user?.user_id;
                  return (
                    <div key={m.id} className={`flex items-end gap-1.5 ${mine ? "justify-end" : "justify-start"}`}>
                      <div data-testid={`thread-msg-${mine ? "me" : "them"}`} className={`max-w-[80%] rounded-2xl px-3.5 py-2 text-sm leading-relaxed whitespace-pre-wrap break-words ${mine ? "bg-ys-sky text-white rounded-br-sm" : "bg-white border border-slate-200 text-slate-700 rounded-bl-sm"}`}>
                        {m.text}
                        <span className={`block text-[10px] mt-0.5 ${mine ? "text-sky-100" : "text-slate-400"}`}>{timeAgo(m.created_at)}</span>
                      </div>
                      {!mine && (
                        <button onClick={() => reportMessage(m)} data-testid={`report-msg-${m.id}`} title="Report to admin" aria-label="Report message"
                          className="shrink-0 p-1 rounded-full text-slate-300 hover:text-red-500 hover:bg-red-50 transition-colors"><Flag className="w-3.5 h-3.5" /></button>
                      )}
                    </div>
                  );
                })}
                {messages.length === 0 && <p className="text-center text-sm text-slate-400 pt-6">Say hello 👋</p>}
              </div>

              {banned ? (
                <div className="p-4 border-t border-slate-100 bg-white flex items-center justify-center gap-2 text-sm text-red-600" data-testid="thread-banned-notice"><ShieldAlert className="w-4 h-4" />Your messaging access has been disabled.</div>
              ) : (
                <div className="p-3 border-t border-slate-100 flex items-center gap-2 shrink-0 bg-white">
                  <Input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                    placeholder="Write a message…" data-testid="thread-input" className="rounded-full h-11" disabled={sending} />
                  <Button onClick={send} disabled={sending || !input.trim()} data-testid="thread-send-btn" className="rounded-full bg-ys-sky hover:bg-sky-700 h-11 w-11 p-0 shrink-0"><Send className="w-4 h-4" /></Button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
